import {
  Typography,
  Stack,
  Button,
  TextField,
  MenuItem,
  Grid,
  Box,
  Divider,
  Chip,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import PersonIcon from "@mui/icons-material/Person";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import PaymentIcon from "@mui/icons-material/Payment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VentaItemRow from "./VentaItemRow";
import VentaSubRow from "./VentaSubRow";
import { etapaLabel } from "./pipelineUtils";
import { SEMANTIC } from "./theme/brand";
import { oportunidadEstaAbierta } from "./expansion/expansionConstants";
import { errorVenta, totalItems as calcularTotal, montosPorModoPago } from "./ventaUtils";
import HistorialCompras from "./HistorialCompras";
import { obtenerFechaHoyISO } from "../../utils/horaLocal";

function SectionHeader({ icon, label, chip }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      {icon}
      <Typography variant="body2" fontWeight={700}>{label}</Typography>
      {chip}
    </Stack>
  );
}

export default function VentaForm({ open, store, form, setForm, productosRecurrentes, onSubmit, onCancel }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const handleChange = (field) => (e) => {
    const nuevo = { ...form, [field]: e.target.value };
    if (field === "vendedorId" && e.target.value) {
      const vend = store.config.vendedores.find((v) => v.id === e.target.value);
      if (vend) nuevo.vendedorNombre = vend.nombre;
    }
    setForm(nuevo);
  };

  const syncItemsToForm = (items) => {
    const total = calcularTotal(items);
    const nuevo = { ...form, items };
    if ((form.modoPago || "efectivo") !== "varios") Object.assign(nuevo, montosPorModoPago(form.modoPago || "efectivo", total));
    setForm(nuevo);
  };

  const handleItemChange = (index, field) => (e) => {
    const items = [...form.items];
    items[index] = { ...items[index], [field]: e.target.value };
    syncItemsToForm(items);
  };

  const handleProductPick = (index, prod) => {
    const items = [...form.items];
    const item = { ...items[index] };
    if (prod) {
      item.productoId = prod.id;
      item.productoNombre = prod.nombre || "";
      item.precioUnitario = prod.precio ?? "";
    } else {
      item.productoId = "";
    }
    items[index] = item;
    syncItemsToForm(items);
  };

  const addItem = () => {
    setForm({ ...form, items: [...form.items, { productoId: "", productoNombre: "", cantidad: 1, precioUnitario: "" }] });
  };

  const removeItem = (index) => {
    if (form.items.length <= 1) return;
    const items = form.items.filter((_, i) => i !== index);
    syncItemsToForm(items);
  };

  const leadSeleccionado = form.leadId ? store.leads.find((l) => l.id === form.leadId) || null : null;
  const oppsCliente = (store.oportunidadesExpansion || []).filter(
    (o) => o.leadId === form.leadId && oportunidadEstaAbierta(o)
  );
  const hoyVenta = obtenerFechaHoyISO();

  const modoPago = form.modoPago || "efectivo";
  const setModoPago = (_, value) => {
    if (!value) return;
    const total = calcularTotal(form.items);
    setForm({
      ...form,
      modoPago: value,
      ...montosPorModoPago(value, total),
      ...(value !== "credito" && value !== "varios" ? { fechaLimitePago: "" } : {}),
    });
  };

  const handleSubDate = (prodId, val) => {
    setForm(prev => ({ ...prev, subDates: { ...prev.subDates, [prodId]: val } }));
  };

  const totalProductos = calcularTotal(form.items);
  const totalPago =
    (parseFloat(form.montoEfectivo) || 0) +
    (parseFloat(form.montoTransferencia) || 0) +
    (parseFloat(form.montoCredito) || 0);
  const montoCredito = parseFloat(form.montoCredito) || 0;
  const pagoIncompleto = totalProductos > 0 && Math.round(totalPago * 100) !== Math.round(totalProductos * 100);
  const necesitaFecha = montoCredito > 0;
  const error = errorVenta(form);
  const isValid = !error;

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3, maxHeight: isMobile ? "100%" : "90vh" } }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          bgcolor: "navy.main",
          color: "#fff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          py: 1.5,
        }}
      >
        Registrar venta
        <IconButton size="small" onClick={onCancel} sx={{ color: "#fff" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: { xs: 2, sm: 2.5 }, py: 2 }}>
      <Stack spacing={2.5}>
        {/* ─── Cliente ─── */}
        <Box>
          <SectionHeader icon={<PersonIcon sx={{ fontSize: 18, color: "primary.main" }} />} label="Cliente" />
          <Grid container spacing={1.5} mt={0.5}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Fecha de venta"
                value={form.fechaVenta || hoyVenta}
                onChange={handleChange("fechaVenta")}
                type="date"
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: hoyVenta }}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <Autocomplete
                options={store.leads}
                value={leadSeleccionado}
                onChange={(_, lead) => setForm((prev) => ({
                  ...prev,
                  leadId: lead?.id || "",
                  nombreCliente: lead?.nombre || prev.nombreCliente,
                  recompraDe: null,
                  renovadaDesde: null,
                  opportunityId: "",
                }))}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                getOptionLabel={(l) => `${l.nombre}${l.telefono ? ` · ${l.telefono}` : ""} — ${etapaLabel(store.config, l.etapa)}`}
                filterOptions={(options, { inputValue }) => {
                  const q = inputValue.trim().toLowerCase();
                  if (!q) return options;
                  return options.filter((l) =>
                    l.nombre?.toLowerCase().includes(q) ||
                    (l.telefono || "").includes(q)
                  );
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Cliente existente" size="small" placeholder="Buscar por nombre o teléfono" />
                )}
                noOptionsText="Sin coincidencias — usa nombre abajo"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Nombre del cliente"
                value={form.nombreCliente}
                onChange={handleChange("nombreCliente")}
                fullWidth
                size="small"
                required
                helperText={leadSeleccionado ? "Puedes ajustar el nombre si hace falta" : "Cliente nuevo o sin vincular al CRM"}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider />

        {form.leadId && <HistorialCompras store={store} leadId={form.leadId} />}

        {form.leadId && oppsCliente.length > 0 && (
          <TextField
            select
            label="Vincular a oportunidad de expansión (opcional)"
            value={form.opportunityId || ""}
            onChange={handleChange("opportunityId")}
            fullWidth
            size="small"
            helperText="Cierra la oportunidad como ganada al registrar la venta"
          >
            <MenuItem value="">Sin oportunidad vinculada</MenuItem>
            {oppsCliente.map((o) => (
              <MenuItem key={o.id} value={o.id}>{o.nombre}</MenuItem>
            ))}
          </TextField>
        )}

        {/* ─── Productos ─── */}
        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <SectionHeader
              icon={<ShoppingCartIcon sx={{ fontSize: 18, color: "primary.main" }} />}
              label="Productos / Servicios"
              chip={form.items.length > 1 ? <Chip label={form.items.length} size="small" color="primary" sx={{ height: 20, fontSize: 11 }} /> : null}
            />
            <Button size="small" startIcon={<AddCircleOutlineIcon />} onClick={addItem} sx={{ textTransform: "none", fontWeight: 600 }}>
              Agregar
            </Button>
          </Stack>
          <Stack spacing={1} mt={1}>
            {form.items.map((item, idx) => (
              <VentaItemRow
                key={idx}
                item={item}
                index={idx}
                total={form.items.length}
                productos={store.config.productos}
                moneda={store.config.moneda}
                onChange={handleItemChange}
                onProductPick={handleProductPick}
                onRemove={removeItem}
                canRemove={form.items.length > 1}
              />
            ))}
          </Stack>
          {totalProductos > 0 && (
            <Typography variant="body2" fontWeight={700} textAlign="right" mt={1} color="text.secondary">
              Subtotal: {store.config.moneda} {totalProductos.toLocaleString()}
            </Typography>
          )}
        </Box>

        {/* Suscripciones recurrentes */}
        {productosRecurrentes.length > 0 && (
          <Stack spacing={1}>
            {productosRecurrentes.map((prod) => (
              <VentaSubRow key={prod.id} prod={prod} subDates={form.subDates} onChangeSubDate={handleSubDate} />
            ))}
          </Stack>
        )}

        <Divider />

        {/* ─── Pago ─── */}
        <Box>
          <SectionHeader icon={<PaymentIcon sx={{ fontSize: 18, color: "primary.main" }} />} label="Forma de pago" />

          <ToggleButtonGroup
            value={modoPago}
            exclusive
            onChange={setModoPago}
            size="small"
            fullWidth
            sx={{ mt: 1, mb: 1.5, flexWrap: "wrap", "& .MuiToggleButton-root": { flex: 1, minWidth: 0, py: 0.75, fontSize: 12, fontWeight: 600, textTransform: "none" } }}
          >
            <ToggleButton value="efectivo">Efectivo</ToggleButton>
            <ToggleButton value="transferencia">Transferencia</ToggleButton>
            <ToggleButton value="credito">Crédito</ToggleButton>
            <ToggleButton value="varios">Varios</ToggleButton>
          </ToggleButtonGroup>

          {necesitaFecha && (
            <TextField
              label="Fecha límite de pago (crédito)"
              value={form.fechaLimitePago || ""}
              onChange={(e) => setForm(prev => ({ ...prev, fechaLimitePago: e.target.value }))}
              type="date"
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
              required
              sx={{ mb: 1.5 }}
            />
          )}

          <Grid container spacing={1.5}>
            {(modoPago === "efectivo" || modoPago === "varios") && (
              <Grid item xs={modoPago === "varios" ? 4 : 12}>
                <TextField
                  label="Efectivo"
                  value={form.montoEfectivo}
                  onChange={handleChange("montoEfectivo")}
                  type="number"
                  fullWidth
                  size="small"
                  error={pagoIncompleto}
                  InputProps={{ startAdornment: <Typography variant="caption" sx={{ mr: 0.5, opacity: 0.7 }}>💵</Typography> }}
                />
              </Grid>
            )}
            {(modoPago === "transferencia" || modoPago === "varios") && (
              <Grid item xs={modoPago === "varios" ? 4 : 12}>
                <TextField
                  label="Transferencia"
                  value={form.montoTransferencia}
                  onChange={handleChange("montoTransferencia")}
                  type="number"
                  fullWidth
                  size="small"
                  error={pagoIncompleto}
                  InputProps={{ startAdornment: <Typography variant="caption" sx={{ mr: 0.5, opacity: 0.7 }}>🏦</Typography> }}
                />
              </Grid>
            )}
            {(modoPago === "credito" || modoPago === "varios") && (
              <Grid item xs={modoPago === "varios" ? 4 : 12}>
                <TextField
                  label="Crédito"
                  value={form.montoCredito}
                  onChange={handleChange("montoCredito")}
                  type="number"
                  fullWidth
                  size="small"
                  error={pagoIncompleto}
                  InputProps={{ startAdornment: <Typography variant="caption" sx={{ mr: 0.5, opacity: 0.7 }}>💳</Typography> }}
                />
              </Grid>
            )}
          </Grid>

          {pagoIncompleto && totalProductos > 0 && (
            <Alert severity="warning" sx={{ mt: 1 }} variant="outlined">
              Suma pagos: {store.config.moneda} {totalPago.toLocaleString()} — debe ser {store.config.moneda} {totalProductos.toLocaleString()}
            </Alert>
          )}
        </Box>

        {store.config.vendedores.length > 0 && (
          <TextField
            label="Vendedor(a)"
            disabled={store.isOwner === false}
            value={form.vendedorId}
            onChange={handleChange("vendedorId")}
            select
            size="small"
            fullWidth
            sx={{ maxWidth: { sm: 300 } }}
          >
            <MenuItem value="">Sin asignar</MenuItem>
            {store.config.vendedores.filter((v) => v.activo).map((v) => (
              <MenuItem key={v.id} value={v.id}>{v.nombre}</MenuItem>
            ))}
          </TextField>
        )}

        {/* ─── Total + acciones ─── */}
        {totalProductos > 0 && (
          <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: montoCredito > 0 ? SEMANTIC.warningTint : SEMANTIC.revenueTint, textAlign: "center" }}>
            <Typography variant="caption" color="text.secondary">Total de la venta</Typography>
            <Typography variant="h5" fontWeight={800} color={montoCredito > 0 ? "warning.main" : "success.main"}>
              {store.config.moneda} {totalProductos.toLocaleString()}
            </Typography>
            {montoCredito > 0 && (
              <Typography variant="caption" color="text.secondary">
                A crédito: {store.config.moneda} {montoCredito.toLocaleString()}
                {(parseFloat(form.montoEfectivo) || 0) + (parseFloat(form.montoTransferencia) || 0) > 0 &&
                  ` · Anticipo: ${store.config.moneda} ${((parseFloat(form.montoEfectivo) || 0) + (parseFloat(form.montoTransferencia) || 0)).toLocaleString()}`}
              </Typography>
            )}
          </Box>
        )}

      </Stack>
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5, borderTop: 1, borderColor: "divider" }}>
        {error && <Typography variant="caption" color="text.secondary" sx={{ mr: "auto" }}>{error}</Typography>}
        <Button onClick={onCancel} sx={{ fontWeight: 600 }}>Cancelar</Button>
        <Button
          variant="contained"
          color="success"
          onClick={onSubmit}
          disabled={!isValid}
          startIcon={<CheckCircleIcon />}
          sx={{ py: isMobile ? 1.2 : undefined, fontWeight: 700 }}
        >
          Registrar venta
        </Button>
      </DialogActions>
    </Dialog>
  );
}
